import {format, isBefore, isValid, parse} from 'date-fns';

import {ERROR_MESSAGES, ERROR_TYPES} from '../constants';

import {formatValidDateToString} from './formatValidDateToString';
import {hasTimeFormat} from './hasTimeFormat';
import {normalizeToLocalMidnight} from './normalizeToLocalMidnight';
import {separatorRegex} from './normalizeSeparators';

import type {ErrorType} from '../constants';
import type {DateValidationParams} from '../typedef';


interface ValidateDateReturnType {
    infoMessage: string,
    errorType?: ErrorType,
}


const isYear = (value: string): boolean => {
    const parsedDate = parse(value, 'yyyy', new Date());
    return isValid(parsedDate);
};

export const checkDateAndGenerateError = ({
    value,
    minDate,
    maxDate,
    dateFormat,
}: DateValidationParams): ValidateDateReturnType => {
    const minDateLocal = normalizeToLocalMidnight(minDate);
    const maxDateLocal = normalizeToLocalMidnight(maxDate);

    const minYear = minDateLocal.getFullYear();
    const maxYear = maxDateLocal.getFullYear();
    const currentDate = formatValidDateToString(new Date(), dateFormat);
    const fullCurrentDate = parse(currentDate, dateFormat, new Date());

    const dateValue = isYear(value) ? `${value}-01-01` : value;

    // Extract year from input for early validation
    // Match 4-digit year pattern (handles yyyy formats) or 2-digit year at start
    const yearMatch = dateValue.match(/(\d{4})|^(\d{2})/);
    if (yearMatch) {
        const inputYear = parseInt(yearMatch[1] || yearMatch[2], 10);
        // For 4-digit years, check directly; skip for 2-digit years as they're ambiguous
        if (yearMatch[1] && (inputYear < minYear || inputYear > maxYear)) {
            const maxDateValue = isValid(fullCurrentDate) && fullCurrentDate.getTime() === maxDateLocal.getTime()
                ? 'today'
                : maxYear.toString();
            return {
                infoMessage: ERROR_MESSAGES.OUT_OF_RANGE(minYear, maxDateValue),
                errorType: inputYear < minYear ? ERROR_TYPES.LESS_THAN_MIN_DATE : ERROR_TYPES.GREATER_THAN_MAX_DATE,
            };
        }
    }

    // Normalize separators in the input to match the format's separators
    // Skip normalization for datetime formats to preserve time components
    const hasTimeComponent = hasTimeFormat(dateFormat);

    let normalizedValue = dateValue;
    let parsedDate: Date;

    if (hasTimeComponent) {
        // For datetime formats, parse directly without normalization
        parsedDate = parse(dateValue, dateFormat, new Date());
    } else {
        // For date-only formats, normalize separators first
        const formatSeparator = dateFormat.match(/[^a-zA-Z]/)?.[0] || '-';
        normalizedValue = dateValue.replace(separatorRegex, formatSeparator);
        parsedDate = parse(normalizedValue, dateFormat, new Date());
    }

    // Validate parsed date
    let isValidAndMatchesInput = isValid(parsedDate);

    if (isValidAndMatchesInput && !hasTimeComponent) {
        // For date-only formats, strictly validate format-back to catch edge cases like year 0001
        const formattedBack = format(parsedDate, dateFormat);
        isValidAndMatchesInput = formattedBack === normalizedValue;
    }

    const parsedDateLocal = isValidAndMatchesInput ? normalizeToLocalMidnight(parsedDate) : null;

    if (!parsedDateLocal) {
        const invalidIndex = dateValue.search(/[a-zA-Z]/);
        return invalidIndex === 1
            ? {
                    infoMessage: ERROR_MESSAGES.INVALID_MONTH_FORMAT,
                    errorType: ERROR_TYPES.INVALID_MONTH_FORMAT,
                }
            : {
                    infoMessage: ERROR_MESSAGES.INVALID_DATE_FORMAT(currentDate),
                    errorType: ERROR_TYPES.INVALID_DATE_FORMAT,
                };
    }

    // Validate the parsed date is within the acceptable range
    if (!(parsedDateLocal.getTime() >= minDateLocal.getTime() && parsedDateLocal.getTime() <= maxDateLocal.getTime())) {
        const maxDateValue = isValid(fullCurrentDate) && fullCurrentDate.getTime() === maxDateLocal.getTime()
            ? 'today'
            : maxYear.toString();
        return {
            infoMessage: ERROR_MESSAGES.OUT_OF_RANGE(minYear, maxDateValue),
            errorType: isBefore(parsedDateLocal, minDateLocal)
                ? ERROR_TYPES.LESS_THAN_MIN_DATE
                : ERROR_TYPES.GREATER_THAN_MAX_DATE,
        };
    }

    return {infoMessage: ''};
};
